import moment from 'moment';

export const getOption = ({ id, name, content = name }) => ({
  key: id,
  value: id,
  content,
  text: name,
});

const getSelectedCount = (value) => (Array.isArray(value) ? value.length : 0);

const getStreetIdsWithSelectedHouses = ({
  streetIds = [],
  houseIds = [],
  houseOptions = [],
}) => {
  if (!streetIds.length || !houseIds.length) {
    return [];
  }

  const selectedHouseIdSet = new Set(houseIds.map(String));
  const streetIdSet = new Set();

  houseOptions.forEach((houseOption) => {
    if (!selectedHouseIdSet.has(String(houseOption.value))) {
      return;
    }

    if (houseOption.streetId === undefined || houseOption.streetId === null) {
      return;
    }

    streetIdSet.add(String(houseOption.streetId));
  });

  if (!streetIdSet.size) {
    return streetIds;
  }

  return streetIds.filter((streetId) => streetIdSet.has(String(streetId)));
};

export const getAddressPayload = ({
  companyId,
  allCompanyAddresses,
  cityId,
  cityIds,
  cityRegionIds = [],
  streetIds,
  houseIds,
  streetOptions,
  houseOptions,
}) => {
  if (allCompanyAddresses) {
    return { companyId, allCompanyAddresses: true, cityId };
  }

  const selectedStreetIds = getStreetIdsWithSelectedHouses({
    streetIds,
    houseIds,
    houseOptions,
  });
  const addressSelection = {
    cityIds,
    cityRegionIds,
    streetIds: selectedStreetIds,
    houseIds,
  };

  if (
    getSelectedCount(cityIds) === 1 &&
    streetOptions.length > 0 &&
    getSelectedCount(selectedStreetIds) === streetOptions.length &&
    houseOptions.length > 0 &&
    getSelectedCount(houseIds) === houseOptions.length
  ) {
    return {
      cityId: cityIds[0],
      ...addressSelection,
    };
  }

  if (
    getSelectedCount(selectedStreetIds) === 1 &&
    getSelectedCount(houseIds) === houseOptions.length
  ) {
    return {
      streetId: selectedStreetIds[0],
      ...addressSelection,
    };
  }

  return {
    companyId,
    ...addressSelection,
  };
};

export const hasFormValues = (values, ignoredFields = []) =>
  Object.entries(values)
    .filter(([fieldName]) => !ignoredFields.includes(fieldName))
    .some(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === 'string') {
        return Boolean(value.trim());
      }

      return value !== undefined && value !== null;
    });

export const hasAddressValues = ({ allCompanyAddresses, values = {} }) =>
  Boolean(
    allCompanyAddresses ||
      (getSelectedCount(values.cityIds) &&
        (getSelectedCount(values.cityRegionIds) ||
          (getSelectedCount(values.streetIds) &&
            getSelectedCount(values.houseIds))))
  );

const periodDateFormats = ['MM.YYYY', 'DD.MM.YYYY', 'YYYY-MM-DD'];

const getObjectValue = (object, path) =>
  path.split('.').reduce((value, key) => value?.[key], object);

const getEntityId = (entity, idKeys = ['id', 'value']) => {
  if (entity && typeof entity === 'object') {
    const idKey = idKeys.find(
      (key) => getObjectValue(entity, key) !== undefined
    );

    return idKey ? getObjectValue(entity, idKey) : undefined;
  }

  return entity;
};

const getArrayValue = (...values) => {
  const value = values.find((item) => Array.isArray(item));

  return value || [];
};

const getFirstDefinedValue = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const getUniqueValues = (values = []) => {
  const valueSet = new Set();

  return values.filter((value) => {
    if (value === undefined || value === null) {
      return false;
    }

    const valueKey = String(value);

    if (valueSet.has(valueKey)) {
      return false;
    }

    valueSet.add(valueKey);
    return true;
  });
};

const getInitialIdArray = (idKeys, ...values) => {
  for (const value of values) {
    const valuesArray = Array.isArray(value) ? value : [value];
    const ids = getUniqueValues(
      valuesArray.map((item) => getEntityId(item, idKeys))
    );

    if (ids.length) {
      return ids;
    }
  }

  return [];
};

const getInitialOption = (entity, idKeys = ['id', 'value']) => {
  const id = getEntityId(entity, idKeys);
  const name =
    entity?.name ||
    entity?.fullName ||
    entity?.description ||
    entity?.cityName ||
    entity?.cityRegionName ||
    entity?.regionName ||
    entity?.streetName ||
    entity?.building ||
    entity?.number;

  return id !== undefined && id !== null && name
    ? {
        ...getOption({ id, name }),
        ...(entity?.streetId !== undefined && {
          streetId: entity.streetId,
          streetName: entity.streetName,
        }),
        ...((entity?.cityRegionId ?? entity?.regionId) !== undefined && {
          regionId: entity.cityRegionId ?? entity.regionId,
        }),
      }
    : undefined;
};

const getInitialOptions = (idKeys, ...values) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map((value) => getInitialOption(value, idKeys))
    .filter(Boolean);

const getPeriodMoment = (value) => {
  if (!value) {
    return undefined;
  }

  if (moment.isMoment(value)) {
    return value;
  }

  const parsedValue = moment(value, periodDateFormats, true);

  return parsedValue.isValid() ? parsedValue : undefined;
};

const getPeriodMonthValue = (schedule, dateKey, monthKey) =>
  schedule.period?.[monthKey] || schedule[monthKey] || schedule[dateKey];

const getIntervalWorkerId = (interval, fallbackWorkerId) =>
  getFirstDefinedValue(
    interval?.workerId,
    interval?.executerId,
    interval?.executorId,
    fallbackWorkerId
  );

const getIntervalDate = (interval, fallbackDate) =>
  getFirstDefinedValue(
    interval?.date,
    interval?.day,
    interval?.workDate,
    fallbackDate
  );

const getIntervalTime = (interval, timeKeys) =>
  getFirstDefinedValue(...timeKeys.map((key) => interval?.[key]));

const addWorkInterval = (workIntervals, workerId, date, interval = {}) => {
  const from = getIntervalTime(interval, ['from', 'startTime', 'timeFrom']);
  const to = getIntervalTime(interval, ['to', 'endTime', 'timeTo']);

  if (workerId === undefined || workerId === null || !date || !from || !to) {
    return;
  }

  const workerKey = String(workerId);

  workIntervals[workerKey] = {
    ...(workIntervals[workerKey] || {}),
    [date]: { from, to },
  };
};

const getNestedIntervals = (item) => {
  if (Array.isArray(item?.intervals)) {
    return item.intervals;
  }

  if (Array.isArray(item?.days)) {
    return item.days;
  }

  if (Array.isArray(item?.workIntervals)) {
    return item.workIntervals;
  }

  return [item];
};

const normalizeWorkIntervals = (source) => {
  const workIntervals = {};

  if (!source) {
    return workIntervals;
  }

  if (Array.isArray(source)) {
    source.forEach((item = {}) => {
      const fallbackWorkerId = getIntervalWorkerId(item);

      getNestedIntervals(item).forEach((interval = {}) => {
        addWorkInterval(
          workIntervals,
          getIntervalWorkerId(interval, fallbackWorkerId),
          getIntervalDate(interval),
          interval
        );
      });
    });

    return workIntervals;
  }

  Object.entries(source).forEach(([workerId, intervalsByDate]) => {
    if (Array.isArray(intervalsByDate)) {
      intervalsByDate.forEach((interval = {}) => {
        addWorkInterval(
          workIntervals,
          getIntervalWorkerId(interval, workerId),
          getIntervalDate(interval),
          interval
        );
      });

      return;
    }

    Object.entries(intervalsByDate || {}).forEach(([date, interval = {}]) => {
      addWorkInterval(
        workIntervals,
        getIntervalWorkerId(interval, workerId),
        getIntervalDate(interval, date),
        interval
      );
    });
  });

  return workIntervals;
};

export const getWorkScheduleByPeriodInitialValues = (schedule) => {
  const safeSchedule = schedule || {};
  const address = safeSchedule.address || {};
  const scheduleMonth =
    getPeriodMoment(
      getPeriodMonthValue(safeSchedule, 'startDate', 'startMonth')
    ) ||
    getPeriodMoment(getPeriodMonthValue(safeSchedule, 'endDate', 'endMonth'));
  const workers = getArrayValue(safeSchedule.workers, safeSchedule.performers);
  const serviceSets = getArrayValue(safeSchedule.serviceSets);
  const cityIdKeys = ['cityId', 'id', 'value'];
  const nestedCityIdKeys = [
    'cityId',
    'city.id',
    'city.value',
    'street.cityId',
    'street.city.id',
    'street.city.value',
  ];
  const streetIdKeys = ['streetId', 'id', 'value'];
  const nestedStreetIdKeys = ['streetId', 'street.id', 'street.value'];
  const houseIdKeys = ['houseId', 'id', 'value'];
  const cityRegionIdKeys = ['cityRegionId', 'regionId', 'id', 'value'];
  const citySources = [
    safeSchedule.cityIds,
    address.cityIds,
    safeSchedule.cities,
    address.cities,
    safeSchedule.city,
    address.city,
    safeSchedule.cityId,
    address.cityId,
  ];
  const nestedCitySources = [
    safeSchedule.streets,
    address.streets,
    safeSchedule.street,
    address.street,
    safeSchedule.houses,
    address.houses,
    safeSchedule.house,
    address.house,
  ];
  const streetSources = [
    safeSchedule.streetIds,
    address.streetIds,
    safeSchedule.streets,
    address.streets,
    safeSchedule.street,
    address.street,
    safeSchedule.streetId,
    address.streetId,
  ];
  const nestedStreetSources = [
    safeSchedule.houses,
    address.houses,
    safeSchedule.house,
    address.house,
  ];
  const houseSources = [
    safeSchedule.houseIds,
    address.houseIds,
    safeSchedule.houses,
    address.houses,
    safeSchedule.house,
    address.house,
    safeSchedule.houseId,
    address.houseId,
  ];
  const cityRegionSources = [
    safeSchedule.cityRegionIds,
    address.cityRegionIds,
    safeSchedule.cityRegionId,
    address.cityRegionId,
    safeSchedule.cityRegion,
    address.cityRegion,
  ];
  const cityIds = getInitialIdArray(cityIdKeys, ...citySources);
  const streetIds = getInitialIdArray(streetIdKeys, ...streetSources);
  const initialCityIds = cityIds.length
    ? cityIds
    : getInitialIdArray(nestedCityIdKeys, ...nestedCitySources);
  const initialStreetIds = streetIds.length
    ? streetIds
    : getInitialIdArray(nestedStreetIdKeys, ...nestedStreetSources);
  const initialHouseIds = getInitialIdArray(houseIdKeys, ...houseSources);
  const initialCityRegionIds = getInitialIdArray(
    cityRegionIdKeys,
    ...cityRegionSources
  );
  const hasScopedAddresses =
    initialCityIds.length ||
    initialCityRegionIds.length ||
    initialStreetIds.length ||
    initialHouseIds.length;
  const allCompanyAddresses = Boolean(
    safeSchedule.allCompanyAddresses ||
      address.allCompanyAddresses ||
      (address.companyId && !hasScopedAddresses)
  );
  const workIntervals = normalizeWorkIntervals(
    getFirstDefinedValue(
      safeSchedule.workIntervals,
      safeSchedule.intervals,
      safeSchedule.workerIntervals
    )
  );

  return {
    periodMonths: scheduleMonth
      ? [scheduleMonth, scheduleMonth.clone()]
      : undefined,
    allCompanyAddresses,
    cityId: initialCityIds[0],
    cityIds: allCompanyAddresses ? [] : initialCityIds,
    cityRegionIds: allCompanyAddresses ? [] : initialCityRegionIds,
    streetIds: allCompanyAddresses ? [] : initialStreetIds,
    houseIds: allCompanyAddresses ? [] : initialHouseIds,
    initialCityOptions: getInitialOptions(
      cityIdKeys,
      safeSchedule.cities,
      address.cities,
      safeSchedule.city,
      address.city
    ),
    initialStreetOptions: getInitialOptions(
      streetIdKeys,
      safeSchedule.streets,
      address.streets,
      safeSchedule.street,
      address.street
    ),
    initialHouseOptions: getInitialOptions(
      houseIdKeys,
      safeSchedule.houses,
      address.houses,
      safeSchedule.house,
      address.house
    ),
    initialCityRegionOptions: getInitialOptions(
      cityRegionIdKeys,
      safeSchedule.cityRegions,
      address.cityRegions,
      safeSchedule.cityRegion,
      address.cityRegion
    ),
    workerIds: getInitialIdArray(
      ['id', 'value', 'workerId', 'executerId', 'executorId'],
      safeSchedule.workerIds,
      workers
    ),
    workers,
    serviceSetIds: getInitialIdArray(
      ['id', 'value', 'serviceSetId'],
      safeSchedule.serviceSetIds,
      serviceSets
    ),
    serviceSets,
    workIntervals,
  };
};

const isWorkScheduleByPeriodFieldAllowed = ({ mode, field, config }) => {
  const modeConfig = config?.[mode] || {};
  const fieldParts = field.split('.');

  for (let index = fieldParts.length; index > 0; index -= 1) {
    const fieldPath = fieldParts.slice(0, index).join('.');

    if (modeConfig[fieldPath] === false) {
      return false;
    }
  }

  return true;
};

export const isWorkScheduleByPeriodFieldEditable = ({
  mode,
  field,
  fieldAccessConfig,
}) =>
  isWorkScheduleByPeriodFieldAllowed({
    mode,
    field,
    config: fieldAccessConfig,
  });

export const shouldUseWorkScheduleByPeriodFieldInitialValue = ({
  mode,
  field,
  initialValueAccessConfig,
}) =>
  isWorkScheduleByPeriodFieldAllowed({
    mode,
    field,
    config: initialValueAccessConfig,
  });
